import React from 'react';
import PropTypes from 'prop-types';
import { LuCheck, LuCode, LuLock, LuShield } from 'react-icons/lu';
import GlassIcon from '../../../components/ui/GlassIcon';
import useAccessText from '../accessText';

const AVATAR_GRADIENTS = {
  active: 'tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30',
  passive: 'tw-from-slate-300 tw-to-slate-500 tw-shadow-slate-500/20',
};

export const initialsOf = (emp) =>
  `${(emp?.eName || '').charAt(0)}${(emp?.eSurname || '').charAt(0)}`.toLocaleUpperCase('tr-TR') || '?';

/** Initials on a glass disc, with a green dot while the person is online. */
export const PersonAvatar = ({ employee, online, passive, size = 'avatarLg' }) => {
  const { label } = useAccessText();
  return (
    <span className="tw-relative tw-shrink-0">
      <GlassIcon gradient={passive ? AVATAR_GRADIENTS.passive : AVATAR_GRADIENTS.active} size={size}>
        {initialsOf(employee)}
      </GlassIcon>
      {online !== undefined && (
        <span
          title={online ? label('list.onlineTitle') : label('list.offlineTitle')}
          className={`tw-absolute -tw-bottom-0.5 -tw-right-0.5 tw-h-3.5 tw-w-3.5 tw-rounded-full tw-ring-2 tw-ring-white dark:tw-ring-[#22262f] ${online ? 'tw-bg-emerald-500' : 'tw-bg-slate-300 dark:tw-bg-slate-600'}`}
        />
      )}
    </span>
  );
};

PersonAvatar.propTypes = { employee: PropTypes.object.isRequired, online: PropTypes.bool, passive: PropTypes.bool, size: PropTypes.string };

export const PassiveTag = () => {
  const { label } = useAccessText();
  return (
    <span className="tw-inline-flex tw-shrink-0 tw-items-center tw-rounded-full tw-bg-slate-500/10 tw-px-2 tw-py-px tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-slate-500 tw-ring-1 tw-ring-inset tw-ring-slate-500/20 dark:tw-text-slate-400">
      {label('list.passive')}
    </span>
  );
};

export const RoleChip = ({ children }) => (
  <span className="tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1 tw-rounded-full tw-bg-indigo-500/10 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-indigo-700 tw-ring-1 tw-ring-inset tw-ring-indigo-500/20 dark:tw-text-indigo-300">
    <LuShield aria-hidden="true" className="tw-h-3 tw-w-3" />
    {children}
  </span>
);

RoleChip.propTypes = { children: PropTypes.node };

/** Colours of the four access states (legend, tags, switches). */
export const STATE_META = {
  role: { dot: 'tw-bg-sky-500', tag: 'tw-bg-sky-500/10 tw-text-sky-700 tw-ring-sky-500/25 dark:tw-text-sky-300', icon: LuLock, tagKey: 'role' },
  special: { dot: 'tw-bg-violet-500', tag: 'tw-bg-violet-500/10 tw-text-violet-700 tw-ring-violet-500/25 dark:tw-text-violet-300', icon: LuCode, tagKey: 'special' },
  granted: { dot: 'tw-bg-emerald-500', tag: 'tw-bg-emerald-500/10 tw-text-emerald-700 tw-ring-emerald-500/25 dark:tw-text-emerald-300', icon: LuCheck, tagKey: 'granted' },
  none: { dot: 'tw-bg-slate-300 dark:tw-bg-slate-600' },
};

export const StateTag = ({ state }) => {
  const { label } = useAccessText();
  const meta = STATE_META[state];
  if (!meta?.tagKey) return null;
  const Icon = meta.icon;
  return (
    <span className={`tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1 tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-ring-1 tw-ring-inset ${meta.tag}`}>
      <Icon aria-hidden="true" className="tw-h-3 tw-w-3" />
      {label(`editor.tag.${meta.tagKey}`)}
    </span>
  );
};

StateTag.propTypes = { state: PropTypes.string.isRequired };

/**
 * On/off switch of one page. A locked page (role / code) shows a lock in the
 * knob and cannot be turned off here.
 */
export const AccessSwitch = ({ on, locked, title, onToggle }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-disabled={locked || undefined}
    onClick={() => !locked && onToggle()}
    title={title}
    aria-label={title}
    className={`tw-relative tw-h-6 tw-w-11 tw-shrink-0 tw-rounded-full tw-transition-colors tw-duration-200 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
      locked ? 'tw-cursor-not-allowed' : 'tw-cursor-pointer'
    } ${on ? (locked ? 'tw-bg-sky-400/70 dark:tw-bg-sky-500/50' : 'tw-bg-teal-500') : 'tw-bg-slate-300 dark:tw-bg-white/15'}`}
  >
    <span
      className={`tw-absolute tw-top-0.5 tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded-full tw-bg-white tw-text-sky-600 tw-shadow-sm tw-transition-[left] tw-duration-200 ${on ? 'tw-left-[1.375rem]' : 'tw-left-0.5'}`}
    >
      {locked && <LuLock aria-hidden="true" className="tw-h-3 tw-w-3" />}
    </span>
  </button>
);

AccessSwitch.propTypes = { on: PropTypes.bool.isRequired, locked: PropTypes.bool, title: PropTypes.string, onToggle: PropTypes.func.isRequired };
